import { useState, useCallback } from 'react';
import api from '../services/api';

/**
 * useUploadLimit - Hook to check and manage daily upload quota
 *
 * Returns:
 * - checkLimit(): Async function that fetches current limit status
 * - limitStatus: Object with { allowed, remaining, limit, current, resetsAt, isExempt }
 * - loading: Boolean indicating if check is in progress
 * - error: Error message if check failed
 */
const useUploadLimit = () => {
  const [limitStatus, setLimitStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const checkLimit = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/documents/upload-limit');

      const status = response.data;
      setLimitStatus(status);

      console.log('📊 Upload limit status:', status);
      return status;
    } catch (err) {
      console.error('Error checking upload limit:', err);

      // Fail open - allow upload if check fails
      const fallbackStatus = {
        allowed: true,
        isExempt: false,
        reason: 'Unable to verify limit - proceeding',
        current: 0,
        limit: 3,
        remaining: 3,
        resetsAt: null
      };

      setLimitStatus(fallbackStatus);
      setError(err.message || 'Failed to check upload limit');

      return fallbackStatus;
    } finally {
      setLoading(false);
    }
  }, []);

  const resetStatus = useCallback(() => {
    setLimitStatus(null);
    setError(null);
  }, []);

  return {
    checkLimit,
    limitStatus,
    loading,
    error,
    resetStatus
  };
};

export default useUploadLimit;
